import { useEffect } from "react";

/**
 * NAVEGAÇÃO POR CONTROLE (D-pad) — o coração da experiência de TV.
 *
 * ⚠️ SÓ MONTA NO MODO TV (ver App.jsx: `{isTV && <TvSpatialNav/>}`). Em telefone/desktop
 * este componente NEM EXISTE na árvore — nenhum listener é adicionado, nada muda no toque.
 *
 * Como funciona: as SETAS movem o foco para o elemento focável mais próximo NAQUELA direção
 * (geometria real, à la Netflix — não a ordem do DOM). OK/Enter "clica" o item focado.
 * Backspace = voltar. Um `<video>` tocando fica de fora (o player trata as próprias teclas).
 *
 * Os CARDS do carrossel são `<div onClick>` — clicáveis, mas não focáveis. Aqui damos
 * `tabindex=0` a eles (só quando o motor roda, ou seja, só na TV), pra poderem receber foco.
 * O `.click()` num desses divs dispara o mesmo onClick que o dedo dispararia (o React ouve
 * o clique real por delegação) — então navegar por controle abre o vídeo igual ao toque.
 */

// Elementos CLICÁVEIS que não são focáveis por natureza (cards de carrossel e ícones do
// cabeçalho com onClick). O motor dá tabindex a eles sob demanda, só na TV, pra o controle
// conseguir chegar — senão o D-pad não alcança "perfil/entrar" e "buscar".
const CLICAVEIS =
  ".movie-carousel-item, .portrait-carousel-item, .square-carousel-item, .collection-carousel-item, .profile-button, .search-button, .carousel-button";
const FOCAVEIS = `a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"]), ${CLICAVEIS}`;

function visivel(el) {
  return el.offsetParent !== null && el.getClientRects().length > 0;
}

function coletar() {
  const els = Array.from(document.querySelectorAll(FOCAVEIS));
  for (const el of els) {
    // card precisa de tabindex pra receber .focus(); damos aqui, sob demanda, só na TV.
    if (el.matches(CLICAVEIS) && !el.hasAttribute("tabindex")) el.tabIndex = 0;
  }
  return els.filter(visivel);
}

function mover(dir) {
  const els = coletar();
  if (!els.length) return;
  const atual = document.activeElement;
  if (!atual || atual === document.body || !els.includes(atual)) {
    els[0].focus();
    return;
  }
  const r = atual.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;

  let melhor = null;
  let melhorScore = Infinity;
  for (const el of els) {
    if (el === atual) continue;
    const b = el.getBoundingClientRect();
    const bx = b.left + b.width / 2;
    const by = b.top + b.height / 2;
    const dx = bx - cx;
    const dy = by - cy;

    // só candidatos que estão DE FATO naquela direção (com uma folga)
    const naDirecao =
      dir === "right" ? dx > 8 : dir === "left" ? dx < -8 : dir === "down" ? dy > 8 : dy < -8;
    if (!naDirecao) continue;

    const horizontal = dir === "left" || dir === "right";
    const principal = horizontal ? Math.abs(dx) : Math.abs(dy);
    const perpendicular = horizontal ? Math.abs(dy) : Math.abs(dx);
    // Sobreposição no eixo perpendicular = "está na mesma linha/coluna visual". Quem
    // sobrepõe é fortemente preferido (mantém o cursor na trilha, à la Netflix); quem não
    // sobrepõe leva uma penalidade grande. É isto que evita pular pra um vizinho torto.
    const sobrepoe = horizontal
      ? Math.min(r.bottom, b.bottom) - Math.max(r.top, b.top) > 0
      : Math.min(r.right, b.right) - Math.max(r.left, b.left) > 0;
    const score = principal + perpendicular * 2 + (sobrepoe ? 0 : 12000);
    if (score < melhorScore) {
      melhorScore = score;
      melhor = el;
    }
  }

  if (melhor) {
    melhor.focus();
    melhor.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }
}

export default function TvSpatialNav() {
  useEffect(() => {
    // foca o primeiro item assim que a tela pinta (deixa o conteúdo carregar antes)
    const t = setTimeout(() => {
      const els = coletar();
      const a = document.activeElement;
      if (els.length && (!a || a === document.body)) els[0].focus();
    }, 500);

    const DIR = { ArrowRight: "right", ArrowLeft: "left", ArrowUp: "up", ArrowDown: "down" };

    const onKey = (e) => {
      const tag = document.activeElement && document.activeElement.tagName;
      const digitando = /INPUT|TEXTAREA|SELECT/.test(tag || "");
      // Quando há um <video> na tela, é a página do player: as setas/Enter viram seek/
      // volume/play-pause e quem cuida é o BunnyPlayer. Aqui a gente SAI do caminho.
      const noPlayer = !!document.querySelector("video");

      if (DIR[e.key] && !digitando && !noPlayer) {
        e.preventDefault();
        mover(DIR[e.key]);
        return;
      }
      if (e.key === "Enter" && !digitando && !noPlayer) {
        const el = document.activeElement;
        if (el && el !== document.body) {
          e.preventDefault();
          el.click();
        }
        return;
      }
      // Voltar do controle — vale em QUALQUER tela (inclusive sair do vídeo pra home).
      if ((e.key === "Backspace" || e.key === "GoBack" || e.key === "BrowserBack") && !digitando) {
        e.preventDefault();
        window.history.back();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return null;
}
